
const {mk, mkLabelledCheckbox} = require('../utils/misc');
const SudokuCell = require('./SudokuCell');
const SudokuStrategy = require('./SudokuStrategy');

const STATE_JOINER = '_';

function SudokuGrid(el) {
    this.el = el;
    this.table = mk('table', el);
    this.setup();
}

Object.assign(SudokuGrid.prototype, {
    shouldValidate: true
});

SudokuGrid.prototype.setup = function () {
    if (this._setup) return;
    this._setup = true;

    this.relations = {};
    this.relationGroups = {};
    this.allCells = [];
    this.rows = [];
    this.columns = [];
    this.subGrids = [];
    this.allSubGrids = [];

    for (let y = 0; y < 9; y++) {
        const row = new SudokuCell.Set(`row${y}`);
        this.rows.push(row);
        const tr = mk('tr', this.table);
        if (y % 3 === 2) {
            tr.className = 'edge';
        }
        for (let x = 0; x < 9; x++) {
            let column = this.columns[x];
            if (!column) {
                column = new SudokuCell.Set(`col${x}`);
                this.columns.push(column);
            }

            const cell = new SudokuCell(mk('td', tr), x, y);

            this.allCells.push(cell);
            row.add(cell);
            column.add(cell);
            this.getSubGrid(x, y).add(cell);

            cell.on('change', () => {
                this.validate(this.shouldValidate);
                const relatives = this.getRelatedCells(cell);
                relatives.forEach(relative => {
                    if (relative.notesPresent) {
                        this.annotateCell(relative, SudokuStrategy.Simple);
                    }
                });
            });

            cell.on('focus', () => {
                if (this.focus) {
                    this.focus.setFocus(false);
                }
                this.highlight(this.getRelatedCells(cell));
                cell.setFocus(true);
                this.focus = cell;
            });

            cell.on('blur', () => {
                if (this.focus === cell) {
                    this.focus = void 0;
                    cell.setFocus(false);
                    this.highlight();
                }
            });
        }
    }

    this.allCells.forEach(c => this.getRelatedCells(c));

    mkLabelledCheckbox('Validate', this.shouldValidate, this.el, (event) => {
        this.setShouldValidate(event.target.checked);
    });

    const resetButton = mk('button', this.el);
    resetButton.innerText = 'RESET';
    resetButton.addEventListener('click', () => {
        this.allCells.forEach((cell) => {
            cell.reset();
        });
    });

    const annotateButton = mk('button', this.el);
    annotateButton.innerText = 'SIMPLE ANNOTATE';
    annotateButton.addEventListener('click', () => {
        this.annotate(SudokuStrategy.Simple);
    });

    const diadButton = mk('button', this.el);
    diadButton.innerText = 'DIAD-AWARE ANNOTATE';
    diadButton.addEventListener('click', () => {
        this.annotate(SudokuStrategy.DiadAware);
    });
};

SudokuGrid.prototype.getState = function () {
    return this.allCells.map(c => c.getValue() || 0).join(STATE_JOINER);
};

SudokuGrid.prototype.loadState = function (stateStr) {
    stateStr.split(STATE_JOINER).forEach((val, i) => {
        if (val === '0') val = null;
        if (!val && val !== null) return;
        this.allCells[i].setValue(val, true);
    });
};

SudokuGrid.prototype.highlight = function (cellSet) {
    this.allCells.forEach(cell => {
        cell.setHighlight(cellSet && cellSet.contains(cell));
    });
};

SudokuGrid.prototype.getRelationGroups = function (cell) {
    if (!this.relationGroups[cell.name]) {
        const groups = [];
        this.relationGroups[cell.name] = groups;
        groups.push(this.getSubGrid(cell.x, cell.y));
        for (let row of this.rows) {
            if (row.contains(cell)) {
                groups.push(row);
                break;
            }
        }
        for (let col of this.columns) {
            if (col.contains(cell)) {
                groups.push(col);
                break;
            }
        }
    }
    return this.relationGroups[cell.name];
};

SudokuGrid.prototype.getRelatedCells = function (cell) {
    if (!this.relations[cell.name]) {
        const relatives = new SudokuCell.Set();
        this.relations[cell.name] = relatives;
        this.getRelationGroups(cell).forEach(group => relatives.addAll(group));
    }
    return this.relations[cell.name];
};

SudokuGrid.prototype.annotate = function (strategy) {
    if (!this.validate(false)) {
        alert('Errors Present');
        return;
    }
    this.allCells.forEach(cell => {
        this.annotateCell(cell, strategy);
    });
};

SudokuGrid.prototype.annotateCell = function (cell, strategy) {
    if (cell.getValue()) {
        return;
    }
    const possibilities = strategy.determinePossibilities(cell, this);
    cell.clearNotes();
    possibilities.forEach((possibility) => cell.addNote(possibility));
};

SudokuGrid.prototype.setShouldValidate = function (shouldValidate) {
    this.shouldValidate = shouldValidate;
    if (!this.shouldValidate) {
        this.allCells.forEach(cell => cell.setValidity(true));
    } else {
        this.validate(true);
    }
};

SudokuGrid.prototype.validate = function (setValidity) {
    if (setValidity) {
        this.allCells.forEach(cell => cell.setValidity(true));
    }
    let rowsValid = this.evaluateCellGroupValidity(this.rows, setValidity);
    let colsValid = this.evaluateCellGroupValidity(this.columns, setValidity);
    let gridsValid = this.evaluateCellGroupValidity(this.allSubGrids, setValidity);
    return rowsValid && colsValid && gridsValid;
};

SudokuGrid.prototype.evaluateCellGroupValidity = function (cellGroup, setValidity) {
    let groupValid = true;
    cellGroup.forEach(cells => {
        const nums = {};
        let valid = true;
        cells.forEach(cell => {
            const val = cell.getValue();
            valid = valid && (val === null || !nums[val]);
            nums[val] = true;
        });
        groupValid = groupValid && valid;
        if (setValidity) {
            cells.forEach(cell => cell.setValidity(cell.getValidity() && valid));
        }
    });
    return groupValid;
};

SudokuGrid.prototype.getSubGrid = function (x, y) {
    x = Math.floor(x / 3);// 2 -> 0; 3 -> 1 etc
    y = Math.floor(y / 3);
    if (!this.subGrids[x]) {
        this.subGrids[x] = [];
    }
    if (!this.subGrids[x][y]) {
        this.subGrids[x][y] = new SudokuCell.Set(`grid[${x},${y}]`);
        this.allSubGrids.push(this.subGrids[x][y]);
    }
    return this.subGrids[x][y];
};

module.exports = SudokuGrid;

